import { Eye, GripVertical, Plus, Trash2, Edit } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useSocialMediaSystem, SocialNetwork } from '../../hooks/useSocialMediaSystem';
import { SocialNetworkForm } from '../../components/social/SocialNetworkForm';
import { 
  Instagram, 
  Facebook, 
  Linkedin, 
  Music, 
  Camera, 
  PinIcon, 
  ExternalLink,
  MessageCircle,
  Send,
  X
} from 'lucide-react';

export default function SocialMediaSettings() {
  const { chatbotPublicId } = useChatbot();
  const { openModal } = useMiniAppModal();
  const { miniApps, updateMiniAppLabel, initialized: miniAppsInitialized, initializeMiniApps } = useMiniApps();
  const { loading, error, initialized, initialize, deleteNetwork, reorderNetworks, toggleNetwork, getSocialNetworks } = useSocialMediaSystem();
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [editingNetwork, setEditingNetwork] = useState<SocialNetwork | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const socialApp = miniApps.find(app => app.id === 'social');
  const socialNetworks = chatbotPublicId ? getSocialNetworks(chatbotPublicId) : [];

  // Initialize both social media system and mini apps
  useEffect(() => {
    console.log('SocialMediaSettings mounted with chatbotPublicId:', chatbotPublicId);
    if (chatbotPublicId) {
      if (!initialized[chatbotPublicId]) {
        console.log('Initializing social media system');
        initialize(chatbotPublicId);
      }
      
      if (!miniAppsInitialized) {
        console.log('Initializing mini apps');
        initializeMiniApps(chatbotPublicId);
      }
    }
  }, [chatbotPublicId, initialized, initialize, miniAppsInitialized, initializeMiniApps]);

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!chatbotPublicId) return;
    
    const newLabel = e.target.value;
    console.log('Updating mini app label:', { chatbotPublicId, newLabel });
    updateMiniAppLabel('social', newLabel, chatbotPublicId);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || !chatbotPublicId) return;

    // Only update the UI during drag, actual reordering happens on drag end
    if (draggedIndex !== index) {
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && chatbotPublicId) {
      // Create a new array with the updated order
      const networks = [...socialNetworks];
      const draggedItem = networks[draggedIndex];
      
      // Remove the dragged item from its original position
      networks.splice(draggedIndex, 1);
      
      // Add it back at the current position
      networks.splice(draggedIndex, 0, draggedItem);
      
      // Extract only the IDs for the reorderNetworks function
      const networkIds = networks.map(network => network.id);
      
      // Update the order in the database
      reorderNetworks(chatbotPublicId, networkIds);
    }
    
    // Reset the dragged index
    setDraggedIndex(null);
  };

  const handleDelete = async (network: SocialNetwork) => {
    if (chatbotPublicId) {
      await deleteNetwork(chatbotPublicId, network.id);
    }
  };

  const handleToggle = async (network: SocialNetwork) => {
    if (chatbotPublicId) {
      await toggleNetwork(chatbotPublicId, network.id);
    }
  };

  if (loading && !initialized) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Social Media Settings</h2>
          <p className="text-gray-400">Configure your social media networks</p>
        </div>
        <button
          onClick={() => openModal('social')}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
        >
          <Eye className="w-4 h-4" />
          Preview
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Mini-app title */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Mini-app title</h3>
        <input
          type="text"
          value={socialApp?.label || ''}
          onChange={handleLabelChange}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Social Media"
        />
      </div>

      {/* Social Networks List */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-white">Social Networks</h3>
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditingNetwork(null);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Network
          </button>
        </div>

        {socialNetworks.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No social networks configured. Add one to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {socialNetworks.map((network, index) => (
              <div
                key={network.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className="bg-white/5 backdrop-blur-xl rounded-lg p-4 border border-white/10 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <GripVertical className="w-5 h-5 text-gray-500 cursor-move" />
                  
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    network.type === 'instagram' ? 'bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400' :
                    network.type === 'tiktok' ? 'bg-black' :
                    network.type === 'linkedin' ? 'bg-blue-600' :
                    network.type === 'facebook' ? 'bg-blue-500' :
                    network.type === 'snapchat' ? 'bg-yellow-400' :
                    network.type === 'pinterest' ? 'bg-red-600' :
                    network.type === 'spotify' ? 'bg-green-600' :
                    network.type === 'whatsapp' ? 'bg-green-500' :
                    network.type === 'discord' ? 'bg-indigo-600' :
                    network.type === 'messenger' ? 'bg-blue-400' :
                    network.type === 'twitter' ? 'bg-black' :
                    'bg-gray-600'
                  }`}>
                    {network.type === 'instagram' ? <Instagram className="w-6 h-6 text-white" /> :
                     network.type === 'tiktok' ? <Camera className="w-6 h-6 text-white" /> :
                     network.type === 'linkedin' ? <Linkedin className="w-6 h-6 text-white" /> :
                     network.type === 'facebook' ? <Facebook className="w-6 h-6 text-white" /> :
                     network.type === 'snapchat' ? <Camera className="w-6 h-6 text-white" /> :
                     network.type === 'pinterest' ? <PinIcon className="w-6 h-6 text-white" /> :
                     network.type === 'spotify' ? <Music className="w-6 h-6 text-white" /> :
                     network.type === 'whatsapp' ? <MessageCircle className="w-6 h-6 text-white" /> :
                     network.type === 'discord' ? <MessageCircle className="w-6 h-6 text-white" /> :
                     network.type === 'messenger' ? <Send className="w-6 h-6 text-white" /> :
                     network.type === 'twitter' ? <X className="w-6 h-6 text-white" /> :
                     <ExternalLink className="w-6 h-6 text-white" />}
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium">
                      {network.name || (network.type.charAt(0).toUpperCase() + network.type.slice(1))}
                    </h4>
                    <p className="text-sm text-gray-400">{network.url}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    <span className="mr-2 text-sm text-gray-400">Active</span>
                    <label className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      network.enabled ? 'bg-indigo-500' : 'bg-gray-700'
                    }`}>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={network.enabled}
                        onChange={() => handleToggle(network)}
                      />
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          network.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </label>
                  </div>
                  
                  <button
                    onClick={() => setEditingNetwork(network)}
                    className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(network)}
                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {(showAddForm || editingNetwork) && (
        <SocialNetworkForm
          network={editingNetwork}
          onClose={() => {
            setShowAddForm(false);
            setEditingNetwork(null);
          }}
        />
      )}
    </div>
  );
}